import { useToast } from "@repo/ui";
import { PasswordConfirmationModal } from "@/components/security/password-confirmation-modal";
import { useApproval } from "@/context/approval-context";
import { handlePermissionError } from "@/lib/utils/handle-permission-error";
import { deleteReceipt } from "@/lib/api/receipts";
import { ApiClientError } from "@/lib/api/client";
import type { Receipt } from "@/lib/types/inventory";

export interface ReceiptDeleteDialogProps {
  receipt: Receipt | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function ReceiptDeleteDialog({
  receipt,
  open,
  onOpenChange,
  onSuccess,
}: ReceiptDeleteDialogProps) {
  const { toast } = useToast();
  const { requestApproval, isApprovalPending } = useApproval();

  if (!receipt) return null;

  const deleteWithToken = async (password: string, approvalToken?: string) => {
    try {
      await deleteReceipt(receipt.id, { password, approvalToken });

      toast({
        title: "Receipt Deleted",
        description: `Receipt ${receipt.invoice_number} has been deleted.`,
        variant: "success",
      });

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      if (error instanceof ApiClientError && error.statusCode === 403) {
        handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: (token) => deleteWithToken(password, token),
          actionDescription: `Delete receipt ${receipt.invoice_number}`,
        });
        return;
      }
      throw error;
    }
  };

  return (
    <PasswordConfirmationModal
      open={open && !isApprovalPending}
      onClose={() => onOpenChange(false)}
      onSuccess={(password) => deleteWithToken(password)}
      title="Delete Receipt - Password Required"
      description={`You are about to delete receipt ${receipt.invoice_number} for ${receipt.inventory?.item_name || "this item"}. This action will adjust inventory and create an audit trail. Enter your password to confirm.`}
      actionLabel="Delete Receipt"
    />
  );
}
